/**
 * CUSTOMER-R4.2 — compact cart/checkout product-finder cards.
 * Reuses canonical search hits; never dumps Prom pipe titles into the DOM.
 */
import { resolveProductTitle } from "./product-title-resolver.js";
import type { ServerSearchHit } from "./search-server-client-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";

const SIZE_RE = /(\d+(?:[.,]\d+)?\s*(?:мл|л|г|кг|шт\.?|ml|g|kg|l))/i;

export const CART_FINDER_DEBOUNCE_MS = 200;
export const CART_FINDER_VISIBLE_LIMIT = 3;
export const CART_FINDER_PAGE_SIZE = 3;
export const CART_FINDER_FETCH_LIMIT = 30;

export type CartFinderCard = {
  id: string;
  name: string;
  brand: string | null;
  weight: string | null;
  meta: string | null;
  priceUah: number | null;
  imageUrl: string | null;
  availability: string;
};

function pipeParts(title: string): string[] {
  return String(title ?? "")
    .split("|")
    .map((p) => p.trim())
    .filter(Boolean);
}

function parseWeight(title: string): string | null {
  const parts = pipeParts(title);
  const fromPart = [...parts].reverse().find((p) => SIZE_RE.test(p));
  if (fromPart) {
    const m = fromPart.match(SIZE_RE);
    return m?.[1]?.replace(/\s+/g, " ").trim() ?? fromPart;
  }
  const m = String(title ?? "").match(SIZE_RE);
  return m?.[1]?.replace(/\s+/g, " ").trim() ?? null;
}

export function formatSearchHitCard(hit: ServerSearchHit, lang: StoreV2Lang = "ua"): CartFinderCard {
  const resolved = resolveProductTitle(
    {
      id: hit.id,
      title: hit.title,
      brand: hit.brand,
    },
    lang,
  );
  const name = String(resolved.title ?? "")
    .replace(/\|/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const brand = String(hit.brand ?? "").trim() || null;
  const weight = parseWeight(hit.title);
  const metaBits = [brand, weight].filter(Boolean);
  return {
    id: hit.id,
    name: name || "Товар",
    brand,
    weight,
    meta: metaBits.length ? metaBits.join(" · ") : null,
    priceUah: hit.price,
    imageUrl: hit.thumbnail,
    availability: hit.availability,
  };
}

export function toCartAddHit(card: CartFinderCard): {
  id: string;
  title: string;
  imageUrl: string | null;
  priceUah: number | null;
  availability: string;
} {
  return {
    id: card.id,
    title: card.name,
    imageUrl: card.imageUrl,
    priceUah: card.priceUah,
    availability: card.availability,
  };
}

export function nextFinderPageOffset(offset: number, total: number, pageSize = CART_FINDER_PAGE_SIZE): number {
  if (total <= pageSize) return 0;
  const next = offset + pageSize;
  return next >= total ? 0 : next;
}
